/**
 * Nosto-CarouselProductBlock.js
 *
 * Solicita la vista Liquid de un bloque hero + carrusel de colores,
 * la inserta e inicializa Flickity, quick buy y tracking.
 */
(function initializeNostoCarouselProductBlock(global) {
    "use strict";

    class NostoCarouselProductBlock {
        constructor({
            hit = null,
            productId = "",
            container = null,
            context = {},
            source = "",
            track = "",
            attributionKey = "",
            viewService = null,
            viewName = "nosto-carousel-block",
            onProductClick = null,
            onError = null,
            debug = false,
        } = {}) {
            this.hit = hit;
            this.productId = this.normalizeProductId(
                productId ||
                hit?.productId ||
                hit?.product_id ||
                hit?.id
            );

            this.container = this.resolveElement(container);
            this.context = { ...context };
            this.source = this.normalizeString(source || context?.source);
            this.track = this.normalizeString(track || context?.track);
            this.attributionKey = this.normalizeString(
                attributionKey || context?.attributionKey
            );
            this.viewService = viewService;
            this.viewName = this.normalizeString(viewName) || "nosto-carousel-block";
            this.onProductClick =
                typeof onProductClick === "function"
                    ? onProductClick
                    : null;
            this.onError =
                typeof onError === "function"
                    ? onError
                    : null;
            this.debug = Boolean(debug);

            this.root = null;
            this.flickity = null;
            this.previousButton = null;
            this.nextButton = null;
            this.carouselSlidesCount = 0;
            this.isDestroyed = false;

            this.boundHandleClick = this.handleClick.bind(this);
            this.boundHandleResize = this.handleResize.bind(this);
        }

        normalizeString(value) {
            return String(value ?? "").trim();
        }

        normalizeProductId(value) {
            return this.normalizeString(value)
                .split("/")
                .filter(Boolean)
                .at(-1) || "";
        }

        resolveElement(target) {
            if (target instanceof Element) return target;
            if (typeof target === "string") {
                return document.querySelector(target);
            }
            return null;
        }

        getHandle() {
            return this.viewService?.getHandleFromHit?.(this.hit) || "";
        }

        async render() {
            if (!this.container || !this.viewService) {
                throw new Error(
                    "NostoCarouselProductBlock requiere container y viewService."
                );
            }

            const handle = this.getHandle();

            if (!handle) {
                throw new Error(
                    `No fue posible obtener el handle del producto ${this.productId}.`
                );
            }

            try {
                const html = await this.viewService.getView({
                    hit: this.hit,
                    handle,
                    productId: this.productId,
                    view: this.viewName,
                    context: this.context,
                    validationSelector:
                        "[data-nosto-carousel-product-block]",
                });

                if (this.isDestroyed) return null;

                const template = document.createElement("template");
                template.innerHTML = String(html || "").trim();

                this.container.replaceChildren(template.content);
                this.root = this.container.querySelector(
                    "[data-nosto-carousel-product-block]"
                );

                if (!this.root) {
                    throw new Error(
                        "La vista no contiene [data-nosto-carousel-product-block]."
                    );
                }

                this.root.dataset.nostoSource = this.source;
                this.root.dataset.nostoTrack = this.track;
                this.root.dataset.nostoOriginalProductId = this.productId;

                this.applyProductCardScopes();
                this.applyProductCardTracking();
                global.initializeProductCards?.(this.root);

                this.initialize();

                return this;
            } catch (error) {
                this.onError?.({
                    error,
                    block: this,
                    hit: this.hit,
                    productId: this.productId,
                });

                throw error;
            }
        }

        getSectionRoot() {
            return this.container?.closest(
                "[data-nosto-carousel-plp]"
            ) || null;
        }

        copyScopeTemplate(role) {
            const sectionRoot =
                this.getSectionRoot();

            const templateScope =
                sectionRoot?.querySelector(
                    `[data-product-card-scope-template="${role}"]`
                );

            const targetScope =
                this.root?.querySelector(
                    `[data-product-card-scope-role="${role}"]`
                );

            if (
                !templateScope ||
                !targetScope
            ) {
                return;
            }

            /*
             * Solo copiamos clases y JSON de configuración.
             *
             * Las variables CSS desktop/mobile viven directamente
             * sobre el scope real, así que no hacemos getComputedStyle()
             * ni escribimos custom properties inline.
             */
            Array.from(
                templateScope.classList
            ).forEach((className) => {
                targetScope.classList.add(
                    className
                );
            });

            targetScope.setAttribute(
                "data-product-card-scope",
                ""
            );

            targetScope
                .querySelector(
                    ":scope > [data-product-card-config]"
                )
                ?.remove();

            const sourceConfig =
                templateScope.querySelector(
                    "[data-product-card-config]"
                );

            if (sourceConfig) {
                const config =
                    document.createElement(
                        "script"
                    );

                config.type =
                    "application/json";

                config.setAttribute(
                    "data-product-card-config",
                    ""
                );

                config.textContent =
                    sourceConfig.textContent;

                targetScope.prepend(
                    config
                );
            }
        }

        applyProductCardScopes() {
            this.copyScopeTemplate(
                "carousel"
            );
        }

        applyProductCardTracking() {
            this.root
                ?.querySelectorAll("[data-product-card]")
                .forEach((card) => {
                    card.dataset.productSource =
                        "nosto-category";
                    card.dataset.nostoTrack =
                        "category";
                    card.dataset.nostoOriginalProductId =
                        this.normalizeProductId(
                            card.dataset.productId ||
                            this.productId
                        );
                });
        }

        initialize() {
            this.root.addEventListener("click", this.boundHandleClick);
            document.addEventListener(
                "theme:resize:width",
                this.boundHandleResize
            );

            this.previousButton =
                this.root.querySelector(
                    "[data-nosto-carousel-prev]"
                );

            this.nextButton =
                this.root.querySelector(
                    "[data-nosto-carousel-next]"
                );

            this.initializeCarousel();
        }

        getFlickityClass() {
            return global.theme?.Flickity || global.Flickity || null;
        }

        initializeCarousel() {
            const track = this.root?.querySelector(
                "[data-nosto-carousel-track]"
            );

            const slides = Array.from(
                track?.querySelectorAll(
                    "[data-nosto-carousel-slide]"
                ) || []
            );

            this.carouselSlidesCount =
                slides.length;

            /*
             * Con 0 o 1 producto no necesitamos
             * inicializar Flickity.
             */
            if (!track || slides.length <= 1) {
                this.updateCarouselControls();
                return;
            }

            const FlickityClass =
                this.getFlickityClass();

            if (
                typeof FlickityClass !==
                "function"
            ) {
                throw new Error(
                    "Flickity no está disponible para el PLP carrusel."
                );
            }

            this.flickity =
                new FlickityClass(
                    track,
                    {
                        cellSelector:
                            "[data-nosto-carousel-slide]",

                        cellAlign: "left",
                        contain: true,
                        wrapAround: false,
                        draggable: true,

                        prevNextButtons: false,
                        pageDots: false,

                        imagesLoaded: false,
                        adaptiveHeight: false,
                        accessibility: true,
                        setGallerySize: true,
                        percentPosition: true,
                    }
                );

            this.flickity.on(
                "select",
                () => {
                    this.updateCarouselControls();
                }
            );

            this.flickity.on(
                "settle",
                () => {
                    this.updateCarouselControls();
                }
            );

            requestAnimationFrame(() => {
                this.flickity?.resize();

                this.updateCarouselControls();
            });
        }

        updateCarouselControls() {
            const hasCarousel =
                Boolean(this.flickity);

            /*
             * Regla solicitada:
             *
             * las flechas solamente existen visualmente
             * cuando hay al menos 3 productos relacionados.
             */
            const shouldShowArrows =
                hasCarousel &&
                this.carouselSlidesCount >= 3;

            const selectedIndex =
                Number(
                    this.flickity?.selectedIndex ||
                    0
                );

            const lastIndex =
                Math.max(
                    0,
                    Number(
                        this.flickity?.slides?.length ||
                        0
                    ) - 1
                );

            if (this.previousButton) {
                this.previousButton.hidden =
                    !shouldShowArrows;

                this.previousButton.disabled =
                    !shouldShowArrows ||
                    selectedIndex <= 0;
            }

            if (this.nextButton) {
                this.nextButton.hidden =
                    !shouldShowArrows;

                this.nextButton.disabled =
                    !shouldShowArrows ||
                    selectedIndex >= lastIndex;
            }
        }

        handleClick(event) {
            const previousButton =
                event.target.closest(
                    "[data-nosto-carousel-prev]"
                );

            if (
                previousButton &&
                this.root.contains(previousButton)
            ) {
                event.preventDefault();
                event.stopPropagation();

                this.flickity?.previous();

                return;
            }

            const nextButton =
                event.target.closest(
                    "[data-nosto-carousel-next]"
                );

            if (
                nextButton &&
                this.root.contains(nextButton)
            ) {
                event.preventDefault();
                event.stopPropagation();

                this.flickity?.next();

                return;
            }

            const productLink = event.target.closest(
                "[data-product-card-link]"
            );

            if (!productLink || !this.root.contains(productLink)) return;

            const card = productLink.closest(
                "[data-product-card]"
            );

            const productId = this.normalizeProductId(
                card?.dataset.productId ||
                productLink.dataset.productId ||
                this.productId
            );

            this.onProductClick?.({
                block: this,
                productId,
                link: productLink,
                hit: this.hit,
                source: this.source,
                track: this.track,
                context: this.context,
            });
        }

        handleResize() {
            this.flickity?.resize();
            this.updateCarouselControls();
        }

        destroy() {
            if (this.isDestroyed) return;

            this.root?.removeEventListener(
                "click",
                this.boundHandleClick
            );

            document.removeEventListener(
                "theme:resize:width",
                this.boundHandleResize
            );

            try {
                this.flickity?.destroy();
            } catch (error) {
                if (this.debug) {
                    console.warn(
                        "[NostoCarouselProductBlock] No fue posible destruir Flickity.",
                        error
                    );
                }
            }

            this.flickity = null;
            this.previousButton = null;
            this.nextButton = null;
            this.carouselSlidesCount = 0;
            this.root = null;
            this.isDestroyed = true;
        }
    }

    global.NostoCarouselProductBlock =
        NostoCarouselProductBlock;
})(window);